/**
 * Regras puras da pré-análise eletrônica, fora do componente (E91.F3).
 *
 * A página passou do teto de linhas do lint ao ganhar o cabeçalho do
 * resultado, as áreas recolhidas e o resumo das consultas; o que não depende de
 * estado de React vem para cá — e fica testável sem renderizar.
 */
import type { ProtocoloFormAcao } from '@/components/ExameEletronico/ProtocoloForm';
import { ptBR } from '@/i18n/pt-BR';
import type { ExameEletronicoCcbData } from '@/lib/exame_eletronico';
import type { ProcessamentoInicialData } from '@/lib/exame_eletronico_processamento';
import type { SecoesAbertas } from '@/pages/PreAnaliseEletronicaSecoes';

const ta = ptBR.exameEletronico.processamentoAutomatico;

export type PorPeca<T> = Readonly<Record<string, T>>;

/**
 * As ações que substituem o resultado na tela (E89.F2, RF05): o automático e as
 * duas verificações manuais. Salvar o protocolo não verifica nada.
 */
export const ACOES_QUE_ATUALIZAM: ReadonlySet<ProtocoloFormAcao> = new Set([
  'processando',
  'verificando',
  'atualizando',
]);

export function resultadoEmAtualizacao(acao: ProtocoloFormAcao | null): boolean {
  return acao !== null && ACOES_QUE_ATUALIZAM.has(acao);
}

/** A frase do que o automático fez. Só é chamada quando ele verificou peça. */
export function mensagemDoExecutado(resultado: ProcessamentoInicialData): string {
  const n = resultado.pecas_verificadas.length;
  return n === 1 ? ta.concluidoUm : ta.concluidoVarios.replace('{n}', String(n));
}

export function secoesIniciaisVazias(): SecoesAbertas {
  return { documentos: false };
}

export function semChave<T>(mapa: PorPeca<T>, chave: string): PorPeca<T> {
  const { [chave]: descartado, ...resto } = mapa;
  void descartado;
  return resto;
}

/** A peça que abre por padrão: a primeira já verificada; senão, a primeira. */
/** O código em tela de cada peça: o rascunho quando houver, o gravado enquanto não houver. */
export function codigosEmTela(
  pecas: readonly ExameEletronicoCcbData[],
  editados: PorPeca<string>,
): Record<string, string> {
  return Object.fromEntries(
    pecas.map((peca) => [
      peca.source_document_id,
      editados[peca.source_document_id] ?? peca.codigo_declarado ?? '',
    ]),
  );
}

export function pecaDeAbertura(pecas: readonly ExameEletronicoCcbData[]): string | null {
  const comQuadro = pecas.find((peca) => peca.exame?.resultado != null);
  return (comQuadro ?? pecas[0])?.source_document_id ?? null;
}
